import {Directive} from '@angular/core';
import {BillingLine} from '../../core/models/billing-line/billing-line.model';
import {PricingItem} from '../../core/models/quote/pricing-item/pricing-item';
import {ProductPricingV2} from 'src/app/core/models/productv2/productpricingv2.model';
import {ProductV2} from '../../core/models/productv2/productv2.model';
import {DevicePricingItem} from '../../core/models/quote/pricing-item/device-pricing-item';
import {ConsumablePricingItem} from '../../core/models/quote/pricing-item/consumable-pricing-item';
import {DiscoPricingItemMapper} from '../../core/models/quote/pricing-item/mapper/disco-pricing-item.mapper';
import {KeyValue} from '@angular/common';
import {generateUniqueId} from '../../core/utils/math-utils';
import {AtomicProductV2} from '../../core/models/productv2/atomic-productv2.model';

@Directive()
export abstract class AbstractRenewalPricingItemInitializer {


    constructor(private pricingItemMapper: DiscoPricingItemMapper) {
    }


    getPricingItemForDuplication(pricingItem: PricingItem, standardProduct: ProductV2, cmcProduct: ProductV2, referential: any, cmcProtocolCode: string, oldAppliedPrice: string): PricingItem {
        let product = this.getProduct(pricingItem, cmcProduct, standardProduct);
        let newPricingItem: PricingItem;
        if (!product) {
            if (pricingItem instanceof DevicePricingItem) {
                newPricingItem = pricingItem.copyWith({
                    isCmc: pricingItem.isCmc && !!cmcProtocolCode,
                    device: pricingItem.device.copyWith({
                        productPricings: this.pricingItemMapper.mapProductPricing(standardProduct?.priceList?.productPricings || []),
                        isCmcWithStandardProductPricing: true,
                        oldAppliedPrice: oldAppliedPrice
                    })
                });
            } else if (pricingItem instanceof ConsumablePricingItem) {
                newPricingItem = pricingItem.copyWith({
                    isCmc: pricingItem.isCmc && !!cmcProtocolCode,
                    consumable: pricingItem.consumable.copyWith({
                        productPricings: this.pricingItemMapper.mapProductPricing(standardProduct?.priceList?.productPricings || []),
                        isCmcWithStandardProductPricing: true,
                        oldAppliedPrice: oldAppliedPrice
                    })
                });
            }
        } else {
            if (pricingItem instanceof DevicePricingItem) {
                newPricingItem = pricingItem.copyWith({
                    device: pricingItem.device.copyWith({
                        isCmcWithStandardProductPricing: product['isCmcWithStandardProductPricing'],
                        productPricings: this.pricingItemMapper.mapProductPricing(product.priceList?.productPricings || []),
                        status: (product instanceof AtomicProductV2) ? product.status : null,
                        oldAppliedPrice: oldAppliedPrice

                    })
                });
            }
            if (pricingItem instanceof ConsumablePricingItem) {
                newPricingItem = pricingItem.copyWith({
                    consumable: pricingItem.consumable.copyWith({
                        isCmcWithStandardProductPricing: product['isCmcWithStandardProductPricing'],
                        productPricings: this.pricingItemMapper.mapProductPricing(product.priceList?.productPricings || []),
                        status: (product instanceof AtomicProductV2) ? product.status : null,
                        oldAppliedPrice: oldAppliedPrice

                    })
                });
            }
            let cc = this.getBillingLineInfo(pricingItem);
            let prefilledInputs = this.getPrefilledInputs(product.priceList.productPricings, referential, cc);

            newPricingItem = newPricingItem.copyWith({
                id: generateUniqueId(),
                prefilledInputs: prefilledInputs,
                isCmc: pricingItem.isCmc && !!cmcProtocolCode
            });
        }
        return newPricingItem;
    }

    private getBillingLineInfo(pricingItem: PricingItem) {
        let cc: Record<string, any> = {};
        cc.serviceFrequency = pricingItem.userInputs.frequency?.key;
        cc.chargingType = pricingItem.userInputs.billingMode?.key;
        cc.numberOfChanges = pricingItem.userInputs.numberOfChange?.key;
        cc.exchangeRule = pricingItem.userInputs.exchangeRule?.key;
        cc.invoicingMode = pricingItem.userInputs.billingMode?.key;
        return cc;
    }

    private getProduct(pricingItem: PricingItem, cmcProduct: ProductV2, standardProduct: ProductV2) {
        if (pricingItem.isCmc) {
            if (cmcProduct && standardProduct) {
                let productPricing = this.findMatchingProductPricing(cmcProduct.priceList?.productPricings, pricingItem);
                if (!productPricing) {
                    this.completeProductPricingsWithProduct(cmcProduct, standardProduct);
                }
            }
            return cmcProduct;
        } else {
            return standardProduct;
        }
    }

    protected completeProductPricingsWithProduct(cmcProduct: ProductV2, product: ProductV2) {
        let frequencyList = Array.from(
            new Set(
                cmcProduct?.priceList.productPricings.flatMap(
                    pp => pp.productPricingDetails['frequency']
                )
            )
        );
        let standardProductPricings = product.priceList?.productPricings.filter(pp => !frequencyList.includes(pp.productPricingDetails['frequency']));
        if (standardProductPricings) {
            standardProductPricings.forEach(pp => {
                pp['type'] = 'STANDARD';
            });
            cmcProduct.priceList.productPricings = cmcProduct.priceList.productPricings.concat(standardProductPricings);
            cmcProduct['isCmcWithStandardProductPricing'] = true;
        }
    }

    protected findMatchingProductPricing(productPricings: ProductPricingV2[], pricingItem: PricingItem) {
        return productPricings?.find(pp => pp.frequency === pricingItem.userInputs['frequency'].value
            && Number(pp.minQuantity) <= pricingItem.userInputs['quantity']
            && Number(pp.maxQuantity) >= pricingItem.userInputs['quantity']
        );
    }

    protected getCommonPrefilledInputs(productPricings: ProductPricingV2[], referential: any, customerContract: BillingLine): Record<string, any> {
        const referentialBillingModes = referential.billingModes;
        const referentialFrequencies = [...referential.frequencies].sort((f1, f2) => f1.order - f2.order);
        const frequencies: KeyValue<string, string>[] = [];
        const billingModes: KeyValue<string, string>[] = [];

        productPricings?.forEach(pp => {
            if (!frequencies.some(e => e.key === pp.frequency)) {
                frequencies.push({ key: pp.frequency, value: pp.frequency });
            }
            if (!billingModes.some(e => e.key === pp.invoicingMode)) {
                const billingMode = referentialBillingModes.find(b => b.key === pp.invoicingMode);
                if (billingMode) {
                    billingModes.push({ key: pp.invoicingMode, value: billingMode.value });
                }
            }
        });

        if (!frequencies.some(f => f.key === customerContract.serviceFrequency)) {
            frequencies.push({ key: customerContract.serviceFrequency, value: customerContract.serviceFrequency });
        }
        if (!billingModes.some(bm => bm.key === customerContract.chargingType)) {
            const billingMode = referentialBillingModes.find(b => b.key === customerContract.chargingType);
            if (billingMode) {
                billingModes.push({ key: customerContract.chargingType, value: billingMode.value });
            }
        }

        return {
            frequencies: this.sortFrequencies(referentialFrequencies, frequencies),
            billingModes: billingModes,
        };
    }

    protected sortFrequencies(referentialFrequencies: any, frequencies: KeyValue<string, string>[]): KeyValue<string, string>[] {
        const sortedFrequencies: KeyValue<string, string>[] = [];
        referentialFrequencies.forEach(referentialFrequency => {
            if (frequencies.some(frequency => frequency.key === referentialFrequency.frequency)) {
                sortedFrequencies.push({
                    key: referentialFrequency.frequency,
                    value: referentialFrequency.frequency
                });
            }
        });
        return sortedFrequencies;
    }

    abstract initUserInputs(customerContract: BillingLine, prefilledInputs: any, pricingItem: PricingItem): Record<string, any>;

    abstract getPrefilledInputs(productPricings: ProductPricingV2[], referential: any, productDetails: Record<string, any>): Record<string, any>;
}
